import { useEffect, useRef, useState, useCallback } from "react";
import API from "../axios";

interface AttributeValue {
    attributeValueName: string;
    attributeValueOdooId: string;
    image: string | null;
    price: number | null;
    hidden?: boolean;
    _id: string;
}

interface Attribute {
    attributeName: string;
    attributeOdooId: number;
    attributeValues: AttributeValue[];
    _id: string;
    hidden: boolean;
}

interface ImageAttributeProps {
    attribute: Attribute;
    datas: AttributeValue[];
    handleImageUpdated: (id: string, image: string) => void;
    handlePriceUpdated: (id: string, price: number) => void;
    handleHiddenUpdatd: Function;
    vehicleId: string;
    vehicleOdooId: number;
    index: number;
}

const ImageAttributeValues = function (props: ImageAttributeProps) {
    const fileUploadRef = useRef<HTMLInputElement>(null);
    const [imageUploading, setImageUploading] = useState(false);
    const [selectedId, setSelectedId] = useState("");
    const [selectedOdooId, setSelectedOdooId] = useState(0);
    const [hidden, setHidden] = useState(props.attribute.hidden);
    const debounceTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});

    // Local state for each image by _id
    const [images, setImages] = useState<Record<string, string>>(() => {
        const initialImages: Record<string, string> = {};
        props.datas.forEach((item) => {
            initialImages[item._id] = item.image || "/images/no-image.png";
        });
        return initialImages;
    });

    // Local state for each price by _id
    const [prices, setPrices] = useState<Record<string, number>>(() => {
        const initialPrices: Record<string, number> = {};
        props.datas.forEach((item) => {
            initialPrices[item._id] = item.price || 0;
        });
        return initialPrices;
    });

    // Local state for each hidden by _id
    const [hiddens, setHiddens] = useState(() => {
        const initialHiddens: Record<string, boolean> = {};
        props.datas.forEach((item) => {
            initialHiddens[item._id] = item.hidden || false;
        });
        return initialHiddens;
    });

    const handleImageChange = async (file: File | undefined) => {
        if (!file || !selectedId || !selectedOdooId) return;

        setImageUploading(true);

        try {
            const result: any = await API.upload(
                file,
                "xcess.com/additional_options"
            );
            const fileUrl = result.fileUrl;

            setImages((prev) => ({
                ...prev,
                [selectedId]: fileUrl,
            }));

            await API.createLocalAttributeImage(selectedOdooId, fileUrl);
            props.handleImageUpdated?.(selectedId, fileUrl);
        } catch (error) {
            console.error("Image upload error:", error);
        }

        setImageUploading(false);
    };

    const handleRemoveImage = async (id: string, odooId: string) => {
        // Update the local state for responsive UI
        setImages((prev) => ({
            ...prev,
            [id]: "/images/no-image.png",
        }));

        try {
            await API.deleteLocalAttributeImage(parseInt(odooId));
        } catch (error) {
            console.log("Image removing error");
        }
    };

    const handlePriceChange = (
        id: string,
        odooId: string,
        newPrice: number
    ) => {
        // Update local state immediately for responsive UI
        setPrices((prev) => ({
            ...prev,
            [id]: newPrice,
        }));

        // Clear existing timeout for this specific item
        if (debounceTimeoutRef.current[id]) {
            clearTimeout(debounceTimeoutRef.current[id]);
        }

        // Set new timeout for API call and parent callback
        debounceTimeoutRef.current[id] = setTimeout(async () => {
            try {
                // You may need to add an API call to update the price on the server
                // await API.updateAttributePrice(parseInt(odooId), newPrice);
                props.handlePriceUpdated?.(id, newPrice);
                await API.createLocalAttributePrice(parseInt(odooId), newPrice);
            } catch (error) {
                console.error("Price update error:", error);
            } finally {
                // Clean up the timeout reference
                delete debounceTimeoutRef.current[id];
            }
        }, 2000);
    };

    const handleHiddenChange = (
        id: string,
        odooId: string,
        newHidden: boolean
    ) => {
        setHiddens((prev) => ({
            ...prev,
            [id]: newHidden,
        }));

        // Clear existing timeout for this specific item
        if (debounceTimeoutRef.current[id]) {
            clearTimeout(debounceTimeoutRef.current[id]);
        }

        // Set new timeout for API call and parent callback
        debounceTimeoutRef.current[id] = setTimeout(async () => {
            try {
                if (newHidden) {
                    await API.createHiddenOption(
                        parseInt(odooId),
                        props.vehicleOdooId
                    );
                } else {
                    await API.deleteHiddenOption(
                        parseInt(odooId),
                        props.vehicleOdooId
                    );
                }
                // await API.createLocalAttributePrice(parseInt(odooId), newPrice);
            } catch (error) {
                console.error("Price update error:", error);
            } finally {
                // Clean up the timeout reference
                delete debounceTimeoutRef.current[id];
            }
        }, 2000);
    };

    const handleUpdateHidden = async (value: boolean) => {
        setHidden(!hidden);

        if (value) {
            await API.createHiddenAdditionalOption(
                props.vehicleId,
                props.index
            );
        } else {
            await API.deleteHiddenAdditionalOption(
                props.vehicleId,
                props.index
            );
        }

        setTimeout(() => {
            props.handleHiddenUpdatd();
        }, 1000);
    };

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            Object.values(debounceTimeoutRef.current).forEach(clearTimeout);
        };
    }, []);

    // Merge new props.datas while preserving existing images and prices
    useEffect(() => {
        setImages((prevImages) => {
            const updatedImages = { ...prevImages };
            props.datas.forEach((item) => {
                if (!updatedImages[item._id]) {
                    updatedImages[item._id] =
                        item.image || "/images/no-image.png";
                }
            });
            return updatedImages;
        });

        setPrices((prevPrices) => {
            const updatedPrices = { ...prevPrices };
            props.datas.forEach((item) => {
                if (updatedPrices[item._id] === undefined) {
                    updatedPrices[item._id] = item.price || 0;
                }
            });
            return updatedPrices;
        });
    }, [props.datas]);

    return (
        <div>
            <table className="table mt-3">
                <thead>
                    <tr>
                        <th>Attribute Value Name</th>
                        <th>Image</th>
                        <th>Price</th>
                        <th>Hidden</th>
                    </tr>
                </thead>
                <tbody>
                    {props.datas.map((attributeValue) => {
                        const isUploadingThisImage =
                            imageUploading && selectedId === attributeValue._id;

                        return (
                            <tr key={attributeValue._id}>
                                <td>{attributeValue.attributeValueName}</td>
                                <td>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            position: "relative",
                                            width: 50,
                                            height: 50,
                                        }}
                                    >
                                        <img
                                            src={images[attributeValue._id]}
                                            alt={
                                                attributeValue.attributeValueName
                                            }
                                            onClick={() => {
                                                if (!imageUploading) {
                                                    setSelectedId(
                                                        attributeValue._id
                                                    );
                                                    setSelectedOdooId(
                                                        parseInt(
                                                            attributeValue.attributeValueOdooId
                                                        )
                                                    );
                                                    fileUploadRef?.current?.click();
                                                }
                                            }}
                                            onError={(e) => {
                                                e.currentTarget.src =
                                                    "/images/no-image.png";
                                            }}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                cursor: imageUploading
                                                    ? "not-allowed"
                                                    : "pointer",
                                                opacity: isUploadingThisImage
                                                    ? 0.5
                                                    : 1,
                                                transition: "opacity 0.2s",
                                                border: "1px solid #ccc",
                                                borderRadius: 5,
                                            }}
                                        />
                                        {isUploadingThisImage && (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background:
                                                        "rgba(255,255,255,0.7)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 10,
                                                    color: "#333",
                                                }}
                                            >
                                                Uploading...
                                            </div>
                                        )}
                                        {/* Small icon button positioned absolutely */}
                                        <button
                                            className="btn btn-danger btn-sm"
                                            style={{
                                                position: "absolute",
                                                top: -5,
                                                right: -5,
                                                width: 20,
                                                height: 20,
                                                minWidth: "unset",
                                                padding: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                borderRadius: "50%",
                                                fontSize: 10,
                                            }}
                                            onClick={() => {
                                                handleRemoveImage(
                                                    attributeValue._id,
                                                    attributeValue.attributeValueOdooId
                                                );
                                            }}
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        value={prices[attributeValue._id] || 0}
                                        onChange={(e) => {
                                            const newPrice =
                                                parseFloat(e.target.value) || 0;
                                            handlePriceChange(
                                                attributeValue._id,
                                                attributeValue.attributeValueOdooId,
                                                newPrice
                                            );
                                        }}
                                        style={{
                                            width: "80px",
                                            padding: "4px 8px",
                                            border: "1px solid #ccc",
                                            borderRadius: "4px",
                                            textAlign: "right",
                                        }}
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                    />
                                </td>
                                <td>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={
                                                hiddens[attributeValue._id] ||
                                                false
                                            }
                                            id="checkIndeterminate"
                                            onChange={(e) => {
                                                const newHidden =
                                                    e.target.checked;
                                                handleHiddenChange(
                                                    attributeValue._id,
                                                    attributeValue.attributeValueOdooId,
                                                    newHidden
                                                );
                                            }}
                                        />
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div className="d-flex justify-content-between p-2">
                <p className="text-sm">Hide this option</p>
                <div className="d-flex flex-row gap-3">
                    <input
                        checked={hidden}
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="flexSwitchCheckDefault"
                        onChange={(e) => {
                            handleUpdateHidden(e.target.checked);
                        }}
                    />

                    <p className="text-base">
                        <small>Hidden</small>
                    </p>
                </div>
            </div>

            <input
                type="file"
                ref={fileUploadRef}
                onChange={(e) => handleImageChange(e.target.files?.[0])}
                style={{ display: "none" }}
                accept="image/*"
            />
        </div>
    );
};

export default ImageAttributeValues;
